import React from 'react';
import styled from '@emotion/styled';
import { colors, mixins } from '../../core/styles/theme/default';
import { Grid, GridItem, Burrito, Hidden, Spacer } from '../../helpers';
import ModuleHeader from '../../primitives/ModuleHeader';
import Link from '../../primitives/Link';
import TeaserCard from '../../components/TeaserCard/TeaserCard';
import MagneticField from '../../components/MagneticField';
import SearchToolbar from '../../components/SearchToolbar';
import { rem } from 'polished';

const StyledTeaserConsulting = styled.aside`
  display: block;
  position: relative;
`;

const StyledDecoration = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  max-height: ${rem(400)};
  height: 90%;
  overflow-y: hidden;
`;

const StyledBurrito = styled(Burrito)`
  padding-top: ${rem(48)};
  z-index: 1;

  .offsetBackground {
    max-height: ${rem(400)};
    background-color: ${colors.blue900};
  }
`;

const StyledLink = styled(Link)`
  .m-richtext & {
    text-decoration: none;
  }
`;

const StyledGridItem = styled(GridItem)`
  ${mixins.responsiveStyles({
    'margin-bottom': {
      zero: `${rem(16)}`,
      medium: `${rem(24)}`,
      ultra: `${rem(32)}`,
    },
  })}
`;

const TeaserConsulting = (teaserConsultingProps: TeaserConsultingProps) => {
  if (teaserConsultingProps === null) return null;

  const {
    title,
    lead,
    items,
    displaySearch,
    selectAllLabel,
    topicOptions,
    action,
    labels,
    showMoreLink,
  } = teaserConsultingProps;

  return (
    <StyledTeaserConsulting>
      <StyledBurrito hasBackground>
        <Hidden below="large">
          <StyledDecoration>
            <MagneticField isVisible />
          </StyledDecoration>
        </Hidden>
        <Grid columns={12}>
          <GridItem size={{ zero: 12, ultra: 8 }} push={{ zero: 0, ultra: 2 }}>
            <ModuleHeader
              title={title || ''}
              lead={lead || undefined}
              color={'light'}
            />
            <Spacer space="deci" />
            {displaySearch !== false ? (
              <>
                <SearchToolbar
                  options={topicOptions}
                  inputLabel={labels.inputLabel}
                  inputPlaceholder={labels.inputPlaceholder}
                  searchFilterText={labels.topicText}
                  searchButtonText={labels.searchButtonText}
                  action={action}
                  selectAllLabel={selectAllLabel}
                />
                <Spacer space="peta" />
              </>
            ) : null}

            {items?.length ? (
              <Grid columns={12}>
                {items.map((data: any, j: number) => (
                  <StyledGridItem
                    size={{ zero: 12, medium: 6, wide: 4, ultra: 6 }}
                    push={0}
                    key={j}
                  >
                    <TeaserCard data={data} variant="consulting" />
                  </StyledGridItem>
                ))}
              </Grid>
            ) : null}
          </GridItem>
        </Grid>
      </StyledBurrito>

      {labels.moreLink ? (
        <Grid columns={12}>
          <GridItem size={{ zero: 12, ultra: 8 }} push={{ zero: 0, ultra: 2 }}>
            <Spacer space={items?.length ? 'beta' : 'hecto'} />
            <StyledLink
              linkText={labels.moreLink}
              link={{
                href: showMoreLink || '',
                title: labels.moreLink,
              }}
              iconAfter="AnimatedIconArrowRight"
              className="animate"
            />
          </GridItem>
        </Grid>
      ) : null}
    </StyledTeaserConsulting>
  );
};

export type TeaserConsultingProps = {
  items: any;
  title?: string;
  lead?: string;
  displaySearch?: boolean;
  selectAllLabel: string;
  topicOptions: any;
  action: string;
  labels: any;
  showMoreLink?: string;
} | null;

export default TeaserConsulting;
